/**
 * PO Header Fields Component
 *
 * Renders the header section of the Purchase Order form.
 * Includes: PO Number, Date, Currency, Payment Terms, Incoterms, and Incoterms Location.
 *
 * This component is used inside POForm and receives react-hook-form
 * context via the form prop.
 *
 * Each field supports confidence-based highlighting:
 * - If AI confidence < 0.8, the field shows a yellow border
 * - This helps users identify fields that need extra attention
 */

import Input from '../common/Input';
import { CURRENCY_OPTIONS } from '../../constants/currencies';
import { isFieldEmpty, normalizeConfidence, getFieldScore, getFieldIssues, unwrapEvaluation } from '../../utils/confidenceUtils';

/**
 * Severity color map for inline issue indicators on select fields
 */
const SEVERITY_COLORS = {
  error:   { border: 'border-l-red-500',   text: 'text-red-600',   icon: 'text-red-500' },
  warning: { border: 'border-l-amber-500', text: 'text-amber-600', icon: 'text-amber-500' },
  info:    { border: 'border-l-blue-500',  text: 'text-blue-600',  icon: 'text-blue-500' },
};

function formatIssueType(type) {
  if (!type) return 'Issue';
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Inline issue indicator for select fields (same pattern as Input.jsx tooltip)
 */
function IssueIndicator({ issues = [] }) {
  if (issues.length === 0) return null;
  const worst = issues.some(i => i.severity === 'error') ? 'error'
    : issues.some(i => i.severity === 'warning') ? 'warning' : 'info';
  return (
    <span className="relative group/issues inline-flex">
      <svg className={`w-3.5 h-3.5 ${SEVERITY_COLORS[worst]?.icon || 'text-amber-500'} cursor-help`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover/issues:block w-64">
        <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
          {issues.map((issue, idx) => (
            <div key={idx} className={`px-3 py-2 border-l-4 ${SEVERITY_COLORS[issue.severity]?.border || 'border-l-gray-300'} ${idx > 0 ? 'border-t border-gray-100' : ''}`}>
              <p className={`text-xs font-semibold ${SEVERITY_COLORS[issue.severity]?.text || 'text-gray-600'}`}>{formatIssueType(issue.type)}</p>
              {issue.detail && <p className="text-xs text-gray-600 mt-0.5">{issue.detail}</p>}
            </div>
          ))}
        </div>
      </div>
    </span>
  );
}

/**
 * Standard Incoterms 2020 options
 */
const INCOTERMS_OPTIONS = [
  { value: '', label: 'Select Incoterms...' },
  { value: 'EXW', label: 'EXW - Ex Works' },
  { value: 'FCA', label: 'FCA - Free Carrier' },
  { value: 'CPT', label: 'CPT - Carriage Paid To' },
  { value: 'CIP', label: 'CIP - Carriage and Insurance Paid To' },
  { value: 'DAP', label: 'DAP - Delivered at Place' },
  { value: 'DPU', label: 'DPU - Delivered at Place Unloaded' },
  { value: 'DDP', label: 'DDP - Delivered Duty Paid' },
  { value: 'FAS', label: 'FAS - Free Alongside Ship' },
  { value: 'FOB', label: 'FOB - Free on Board' },
  { value: 'CFR', label: 'CFR - Cost and Freight' },
  { value: 'CIF', label: 'CIF - Cost, Insurance and Freight' }
];

/**
 * POHeaderFields Component
 *
 * @param {Object} props - Component props
 * @param {Object} props.form - React Hook Form instance
 * @param {Object} props.confidence - Confidence scores from AI extraction
 *
 * @example
 * <POHeaderFields
 *   form={form}
 *   confidence={{ 'header.poNumber': 0.95, 'header.date': 0.7 }}
 * />
 */
function POHeaderFields({ form, evaluation: rawEvaluation, confidence = {}, issues = [] }) {
  const evaluation = unwrapEvaluation(rawEvaluation);
  // Destructure form methods
  const { register, formState: { errors }, watch } = form;

  // Watch header values so we can hide badges on empty, confidently-absent fields
  const header = watch('header');

  /**
   * Get score for a header field from evaluation (new) or confidence (legacy).
   * Hides the badge when the field is empty and the score indicates
   * confident absence (legacy 0.0-on-empty normalizes to 0.95).
   * @param {string} key - Field path, e.g. 'header.poNumber'
   * @returns {number|undefined}
   */
  const getHeaderConfidence = (key) => {
    // Try LLM-as-judge evaluation first, then legacy confidence
    const score = getFieldScore(evaluation, confidence, key);
    if (score == null) return undefined;
    // key is 'header.fieldName' — grab the fieldName part
    const fieldPart = key.split('.').slice(1).join('.');
    const val = fieldPart.split('.').reduce((obj, k) => obj?.[k], header);
    return normalizeConfidence(score, isFieldEmpty(val));
  };

  return (
    <div className="space-y-4">
      {/* Section Title */}
      <h3 className="text-lg font-medium text-gray-900 border-b pb-2">
        Header Information
      </h3>

      {/* Two column layout for header fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* PO Number - Required field */}
        <Input
          label="PO Number"
          {...register('header.poNumber', {
            required: 'PO Number is required'
          })}
          error={errors.header?.poNumber}
          confidence={getHeaderConfidence('header.poNumber')}
          issues={getFieldIssues(issues, 'header.poNumber')}
        />

        {/* Date - Optional but common */}
        <Input
          label="Date"
          type="date"
          {...register('header.date')}
          error={errors.header?.date}
          confidence={getHeaderConfidence('header.date')}
          issues={getFieldIssues(issues, 'header.date')}
        />

        {/* Currency - Dropdown with ISO 4217 codes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <span className="inline-flex items-center gap-1">
              Currency
              {getHeaderConfidence('header.currency') !== undefined && (
                <span className={`text-xs ${getHeaderConfidence('header.currency') >= 0.8 ? 'text-green-600' : 'text-yellow-600'}`}>
                  ({Math.round(getHeaderConfidence('header.currency') * 100)}%)
                </span>
              )}
              <IssueIndicator issues={getFieldIssues(issues, 'header.currency')} />
            </span>
          </label>
          <select
            {...register('header.currency')}
            className={`
              w-full px-3 py-2 border rounded-md shadow-sm text-sm
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
              ${getHeaderConfidence('header.currency') !== undefined && getHeaderConfidence('header.currency') < 0.8
                ? 'border-yellow-400 bg-yellow-50'
                : 'border-gray-300'
              }
              ${errors.header?.currency ? 'border-red-500' : ''}
            `}
          >
            {CURRENCY_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors.header?.currency && (
            <p className="mt-1 text-sm text-red-600">{errors.header.currency.message}</p>
          )}
        </div>

        {/* Payment Terms */}
        <Input
          label="Payment Terms"
          {...register('header.paymentTerms')}
          error={errors.header?.paymentTerms}
          confidence={getHeaderConfidence('header.paymentTerms')}
          issues={getFieldIssues(issues, 'header.paymentTerms')}
        />

        {/* Required By Date */}
        <Input
          label="Required By"
          type="date"
          {...register('header.requiredBy')}
          error={errors.header?.requiredBy}
          confidence={getHeaderConfidence('header.requiredBy')}
          issues={getFieldIssues(issues, 'header.requiredBy')}
        />

        {/* Incoterms */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <span className="inline-flex items-center gap-1">
              Incoterms
              {getHeaderConfidence('header.incoterms') !== undefined && (
                <span className={`text-xs ${getHeaderConfidence('header.incoterms') >= 0.8 ? 'text-green-600' : 'text-yellow-600'}`}>
                  ({Math.round(getHeaderConfidence('header.incoterms') * 100)}%)
                </span>
              )}
              <IssueIndicator issues={getFieldIssues(issues, 'header.incoterms')} />
            </span>
          </label>
          <select
            {...register('header.incoterms')}
            className={`
              w-full px-3 py-2 border rounded-md shadow-sm text-sm
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
              ${getHeaderConfidence('header.incoterms') !== undefined && getHeaderConfidence('header.incoterms') < 0.8
                ? 'border-yellow-400 bg-yellow-50'
                : 'border-gray-300'
              }
              ${errors.header?.incoterms ? 'border-red-500' : ''}
            `}
          >
            {INCOTERMS_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors.header?.incoterms && (
            <p className="mt-1 text-sm text-red-600">{errors.header.incoterms.message}</p>
          )}
        </div>

        {/* Incoterms Location */}
        <Input
          label="Incoterms Location"
          {...register('header.incotermsLocation')}
          error={errors.header?.incotermsLocation}
          confidence={getHeaderConfidence('header.incotermsLocation')}
          issues={getFieldIssues(issues, 'header.incotermsLocation')}
        />
      </div>
    </div>
  );
}

export default POHeaderFields;
